"use client"

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Layers, CircleHelp, X, ChevronLeft, ChevronRight, ChevronDown, ImageOff, Camera, Download, FileText, Check } from 'lucide-react'; 
import axiosInstance from '@/api/axios';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import bananaIcon from './images/banana.png';
import appleIcon from './images/apple.png';
import plasticBottleIcon from './images/plastic-bottle.png';
import metalCanIcon from './images/metal-can.png';
import plasticBagIcon from './images/plastic-bag.png';
import plasticBagColorIcon from './images/plastic-bag-color.png';
import plasticBagSilverIcon from './images/plastic-bag-silver.png';
import organicMandarinIcon from './images/organic-mandarin.png';
import paperBoxIcon from './images/paper-box.png';
import paperCartonIcon from './images/paper-carton.png';
import paperColorIcon from './images/paper-color.png';
import paperTissueIcon from './images/paper-tissue.png';
import paperOfficeIcon from './images/paper-office.png';
import plasticBagWrapperIcon from './images/plastic-bag-wrapper.png';
import plasticContainerIcon from './images/plastic-container.png';
import drinkCartonIcon from './images/drink-carton.png';
import juicePackageIcon from './images/juice-package.png';
import rubberGlovesIcon from './images/rubber-gloves.png';
import paperCoffeeCupIcon from './images/papercoffeecup.png';
import glassBottleIcon from './images/glass-bottle.png';
import cardboardIcon from './images/cardboard.png';
import organicIcon from './images/organic.png';
import meatIcon from './images/meat.png';
import containerIcon from './images/container.png';
import paperIcon from './images/paper.png';
import metalIcon from './images/metal.png';
import plasticIcon from './images/plastic.png';
import recycleIcon from './images/recycle.png';

// ── Normalize for comparison ──────────────────────────────────────────────────
const normalizeForMatch = (s) => (s || '').toLowerCase().replace(/[\s\-_]/g, '');

const mediaMatchesSubclass = (item, subclassName) => {
  if (!subclassName) return false;
  const cardNorm = normalizeForMatch(subclassName);
  if (item.sub_class) return normalizeForMatch(item.sub_class) === cardNorm;
  const label = (item.label || '').toLowerCase();
  return label.includes(cardNorm) || label.includes(cardNorm.replace(/\-/g, ' '));
};

const formatCapture = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam',
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ items, index, onClose, onPrev, onNext, signedUrls, onRequestSign }) {
  const item = items[index];

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowLeft')  onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    const wanted = [items[index], items[index - 1], items[index + 1]]
      .filter(Boolean).map(i => i.file_path);
    onRequestSign(wanted);
  }, [index, items, onRequestSign]);

  const src = signedUrls[item.file_path];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <button onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
                 color: 'rgba(255,255,255,0.8)', cursor: 'pointer', padding: 4 }}>
        <X size={24} />
      </button>
      {items.length > 1 && (
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }}
          style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                   background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                   color: '#fff', cursor: 'pointer', padding: 8 }}>
          <ChevronLeft size={20} />
        </button>
      )}
      <div
        style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                 alignItems: 'center', gap: 12 }}
        onClick={(e) => e.stopPropagation()}
      >
        {src ? (
          item.media_type === 'video'
            ? <video controls autoPlay style={{ maxWidth: '100%', maxHeight: '78vh', borderRadius: 8 }} src={src} />
            : <img src={src} alt={item.label || 'media'}
                   style={{ maxWidth: '100%', maxHeight: '78vh', borderRadius: 8, objectFit: 'contain' }} />
        ) : (
          <div className="animate-pulse" style={{ width: 400, height: 300, maxWidth: '90vw',
                        borderRadius: 8, background: 'rgba(255,255,255,0.1)' }} />
        )}
        <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, textAlign: 'center' }}>
          {item.label && <div style={{ fontWeight: 600, color: '#fff' }}>{item.label}</div>}
          <div>{formatCapture(item.captured_at)}</div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
            {index + 1} / {items.length}
          </div>
        </div>
      </div>
      {items.length > 1 && (
        <button onClick={(e) => { e.stopPropagation(); onNext(); }}
          style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                   background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                   color: '#fff', cursor: 'pointer', padding: 8 }}>
          <ChevronRight size={20} />
        </button>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const SubclassCardView = ({ 
  id, 
  dayvalue, 
  datevalue = [{ startDate: null, endDate: null, key: 'selection' }],
  isCustomDateRange,
  wasteweight = 0,
  totalwasteproduceddata = 0,
  className = "",
  isAdmin = true,
}) => {
  const { t } = useTranslation();

  const exportRef = useRef(null);
  const gridRef = useRef(null);
  
  const [wasteData, setWasteData]                 = useState([]);
  const [wasteTypeWeights, setWasteTypeWeights]   = useState({});
  const [subclassData, setSubclassData]           = useState(null);
  const [actualTotalWeight, setActualTotalWeight] = useState(0);
  const [loading, setLoading]                     = useState(true);
  const [error, setError]                         = useState(null);
  const [displayMode, setDisplayMode]             = useState('kg'); 
  const [selectedCategory, setSelectedCategory]   = useState('All');
  const [exporting, setExporting]                 = useState(false);

  // ── Media state ───────────────────────────────────────────────────────────
  const [allMedia, setAllMedia]           = useState([]);
  const [allCleanMedia, setAllCleanMedia] = useState([]);
  const [imageOverlay, setImageOverlay]   = useState(null);
  const [lightbox, setLightbox]           = useState(null);
  const overlayHideTimer                  = useRef(null);

  const [signedUrls, setSignedUrls]     = useState({});
  const [visibleCount, setVisibleCount] = useState(9);
  const signingInFlight = useRef(new Set());

  const [isDarkMode, setIsDarkMode] = useState(false);
  useEffect(() => {
    const check = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const requestSign = useCallback((paths) => {
    const unique = [...new Set(paths)].filter(
      p => p && !signedUrls[p] && !signingInFlight.current.has(p)
    );
    if (!unique.length) return;
    const batch = unique.slice(0, 9);
    batch.forEach(p => signingInFlight.current.add(p));
    axiosInstance.get('/api/media/sign-batch/', { params: { paths: batch.join(',') } })
      .then(res => setSignedUrls(prev => ({ ...prev, ...res.data.urls })))
      .catch(() => {})
      .finally(() => batch.forEach(p => signingInFlight.current.delete(p)));
  }, [signedUrls]);

  // ── PNG export ────────────────────────────────────────────────────────────
  const handleExportPNG = async () => {
    if (!exportRef.current || exporting) return;
    setExporting(true);
    setImageOverlay(null);
    await new Promise(r => setTimeout(r, 150));

    const container = exportRef.current;
    const scrollable = gridRef.current;

    const origContainerCssText = container.style.cssText;
    const origScrollableCssText = scrollable ? scrollable.style.cssText : '';

    try {
      container.style.setProperty('height', 'max-content', 'important');
      container.style.setProperty('overflow', 'visible', 'important');
      if (scrollable) {
        scrollable.style.setProperty('height', 'max-content', 'important');
        scrollable.style.setProperty('overflow', 'visible', 'important');
      }

      await new Promise(r => setTimeout(r, 100));

      const domToImage = (await import('dom-to-image-more')).default;
      const dataUrl = await domToImage.toPng(container, {
        scale: 2,
        bgcolor: isDarkMode ? '#0f172a' : '#ffffff',
        filter: (node) => !node.classList?.contains('subclass-overlay-ignore'),
      });

      const link = document.createElement('a');
      const dateLabel = isCustomDateRange
        ? `${new Date(datevalue[0]?.startDate).toISOString().slice(0,10)}_${new Date(datevalue[0]?.endDate).toISOString().slice(0,10)}`
        : (dayvalue || 'data');
      link.download = `sub_waste_treemap_${dateLabel}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('PNG export failed:', err);
    } finally {
      container.style.cssText = origContainerCssText;
      if (scrollable) scrollable.style.cssText = origScrollableCssText;
      setExporting(false);
    }
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  const buildCSV = useCallback(() => {
    const binList = Array.isArray(id)
      ? id.join(', ')
      : (typeof id === 'string' ? id : String(id));

    let periodLabel = '';
    if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
      const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      periodLabel = `${fmt(datevalue[0].startDate)} – ${fmt(datevalue[0].endDate)}`;
    } else {
      periodLabel = dayvalue || 'N/A';
    }

    // ── Category totals for summary ───────────────────────────────────────
    const categoryTotals = {};
    wasteData.forEach(item => {
      if (!categoryTotals[item.wasteType]) categoryTotals[item.wasteType] = 0;
      categoryTotals[item.wasteType] += item.weight;
    });
    const grandTotal = wasteData.reduce((s, i) => s + i.weight, 0);

    let csv = '';

    // ── Section 1: Summary ────────────────────────────────────────────────
    csv += `=== SUB-WASTE TREEMAP SUMMARY ===\n`;
    csv += `Bins,${binList}\n`;
    csv += `Period,${periodLabel}\n`;
    csv += `Total Weight (kg),${grandTotal.toFixed(3)}\n`;
    csv += `Total Items,${wasteData.length}\n`;
    csv += `\n`;

    // ── Section 2: Per-category breakdown ─────────────────────────────────
    csv += `=== CATEGORY BREAKDOWN ===\n`;
    csv += `Category,Weight (kg),Share of Total (%),Item Count\n`;
    Object.entries(categoryTotals)
      .sort((a, b) => b[1] - a[1])
      .forEach(([cat, weight]) => {
        const share = grandTotal > 0 ? ((weight / grandTotal) * 100).toFixed(1) : '0.0';
        const count = wasteData.filter(i => i.wasteType === cat).length;
        csv += `"${cat}",${weight.toFixed(3)},${share},${count}\n`;
      });
    csv += `\n`;

    // ── Section 3: Full item detail ───────────────────────────────────────
    csv += `=== ITEM DETAIL ===\n`;
    csv += `Item Name,Found in Category,% of Category,Weight (kg),% of Total,Status\n`;
    wasteData.forEach(item => {
      const pctOfCat   = wasteTypeWeights[item.wasteType] > 0
        ? ((item.weight / wasteTypeWeights[item.wasteType]) * 100).toFixed(1)
        : item.percentageOfCategory.toFixed(1);
      const pctOfTotal = grandTotal > 0
        ? ((item.weight / grandTotal) * 100).toFixed(1)
        : item.percentageOfTotal.toFixed(1);
      let statusLabel = '-';
      if (item.contaminatedWeight > 0) statusLabel = 'Contamination';
      else if (item.cleanWeight > 0) statusLabel = 'Correct stream';
      csv += `"${item.name}","${item.wasteType}",${pctOfCat},${item.weight.toFixed(3)},${pctOfTotal},${statusLabel}\n`;
    });

    return csv;
  }, [wasteData, wasteTypeWeights, id, dayvalue, isCustomDateRange, datevalue]);

  const handleExportCSV = useCallback(() => {
    if (!wasteData.length) return;
    const csv = buildCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateLabel = isCustomDateRange
      ? `${new Date(datevalue[0]?.startDate).toISOString().slice(0,10)}_${new Date(datevalue[0]?.endDate).toISOString().slice(0,10)}`
      : (dayvalue || 'data');
    link.download = `sub_waste_treemap_${dateLabel}.csv`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  }, [buildCSV, wasteData.length, isCustomDateRange, datevalue, dayvalue]);

  // ── Keep data-export-data in sync so DashboardExport can grab it ─────────
  const exportDataAttr = useMemo(() => {
    if (!wasteData.length) return null;
    const grandTotal = wasteData.reduce((s, i) => s + i.weight, 0);
    return JSON.stringify({
      title: 'Sub-Waste Treemap',
      headers: ['Item Name', 'Found in Category', '% of Category', 'Weight (kg)', '% of Total'],
      rows: wasteData.map(item => {
        const pctOfCat   = wasteTypeWeights[item.wasteType] > 0
          ? ((item.weight / wasteTypeWeights[item.wasteType]) * 100).toFixed(1)
          : item.percentageOfCategory.toFixed(1);
        const pctOfTotal = grandTotal > 0
          ? ((item.weight / grandTotal) * 100).toFixed(1)
          : item.percentageOfTotal.toFixed(1);
        return [item.name, item.wasteType, pctOfCat, item.weight.toFixed(3), pctOfTotal];
      }),
    });
  }, [wasteData, wasteTypeWeights]);

  const buildMediaParams = useCallback(() => {
    const params = new URLSearchParams();
    const binIdentifiers = Array.isArray(id)
      ? id
      : (typeof id === 'string' && id.includes(',') ? id.split(',') : [id]);
    binIdentifiers.map(b => String(b).trim()).forEach(b => params.append('bin', b));

    if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
      const sDate = new Date(datevalue[0].startDate);
      const eDate = new Date(datevalue[0].endDate);
      if (!isNaN(sDate) && !isNaN(eDate)) {
        const fmt = (d) => {
          const pad = (n) => String(n).padStart(2, '0');
          return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
        };
        params.append('start_date', fmt(sDate));
        params.append('end_date',   fmt(eDate));
      }
    } else {
      const periodMap = {
        'day': 'day', 'week': 'week', 'month': 'month',
        '3month': '3months', '3months': '3months',
        '6month': '6months', '6months': '6months',
        'year': 'year',
      };
      params.append('period', periodMap[(dayvalue || '').toLowerCase()] || 'week');
    }
    return params.toString();
  }, [id, dayvalue, isCustomDateRange, datevalue]);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const qs = buildMediaParams();
        const [contamRes, cleanRes] = await Promise.all([
          axiosInstance.get(`contamination_media/?${qs}`),
          axiosInstance.get(`clean_media/?${qs}`),
        ]);
        setAllMedia(contamRes.data?.media ?? []);
        setAllCleanMedia(cleanRes.data?.media ?? []);
      } catch {
        setAllMedia([]);
        setAllCleanMedia([]);
      }
    };
    fetchMedia();
  }, [buildMediaParams]);

  const showOverlay = useCallback((subclassName, wasteType, e) => {
    clearTimeout(overlayHideTimer.current);
    const contamImages = allMedia.filter(m => mediaMatchesSubclass(m, subclassName));
    const cleanImages  = allCleanMedia.filter(m => mediaMatchesSubclass(m, subclassName));
    const images = [...contamImages, ...cleanImages];
    setImageOverlay({ visible: true, x: e.clientX, y: e.clientY, subclassName, wasteType, images });
  }, [allMedia, allCleanMedia]);

  const scheduleHideOverlay = useCallback(() => {
    overlayHideTimer.current = setTimeout(() => setImageOverlay(null), 400);
  }, []);

  const cancelHideOverlay = useCallback(() => {
    clearTimeout(overlayHideTimer.current);
  }, []);

  useEffect(() => { setVisibleCount(9); }, [imageOverlay?.subclassName, imageOverlay?.wasteType]);

  useEffect(() => {
    if (!imageOverlay?.images?.length) return;
    requestSign(imageOverlay.images.slice(0, visibleCount).map(img => img.file_path));
  }, [imageOverlay, visibleCount, requestSign]);

  const getIconPath = (subclassName) => {
    if (!subclassName) return recycleIcon;
    const name = subclassName.toLowerCase().replace(/\s+/g, '-');
    const iconMap = {
      'banana': bananaIcon, 'apple': appleIcon, 'plastic-bottle': plasticBottleIcon,
      'metal-can': metalCanIcon, 'plastic-bag': plasticBagIcon, 'plastic-bag-color': plasticBagColorIcon,
      'plastic-bag-silver': plasticBagSilverIcon, 'organic-mandarin': organicMandarinIcon,
      'paper-box': paperBoxIcon, 'paper-carton': paperCartonIcon, 'paper-color': paperColorIcon,
      'paper-tissue': paperTissueIcon, 'paper-office': paperOfficeIcon,
      'plastic-wrapper': plasticBagWrapperIcon, 'plastic-bag-wrapper': plasticBagWrapperIcon,
      'plastic-container': plasticContainerIcon, 'drink-carton': drinkCartonIcon,
      'juice-package': juicePackageIcon, 'rubber-gloves': rubberGlovesIcon,
      'coffee-cup': paperCoffeeCupIcon, 'paper-coffee-cup': paperCoffeeCupIcon,
      'glass-bottle': glassBottleIcon, 'cardboard': cardboardIcon, 'organic': organicIcon,
      'meat': meatIcon, 'container': containerIcon, 'paper': paperIcon,
      'metal': metalIcon, 'plastic': plasticIcon, 'teabag': organicIcon,
    };
    if (iconMap[name]) return iconMap[name];
    if (name.includes('banana')) return bananaIcon;
    if (name.includes('apple')) return appleIcon;
    if (name.includes('bottle') && name.includes('plastic')) return plasticBottleIcon;
    if (name.includes('bottle') && name.includes('glass')) return glassBottleIcon;
    if (name.includes('can') || name.includes('metal')) return metalCanIcon;
    if (name.includes('bag') && name.includes('silver')) return plasticBagSilverIcon;
    if (name.includes('bag') && name.includes('wrapper')) return plasticBagWrapperIcon;
    if (name.includes('bag') && name.includes('color')) return plasticBagColorIcon;
    if (name.includes('bag')) return plasticBagIcon;
    if (name.includes('paper') && name.includes('box')) return paperBoxIcon;
    if (name.includes('paper') && name.includes('carton')) return paperCartonIcon;
    if (name.includes('paper') && name.includes('color')) return paperColorIcon;
    if (name.includes('paper') && name.includes('tissue')) return paperTissueIcon;
    if (name.includes('paper') && name.includes('office')) return paperOfficeIcon;
    if (name.includes('coffee')) return paperCoffeeCupIcon;
    if (name.includes('mandarin') || name.includes('orange')) return organicMandarinIcon;
    if (name.includes('wrapper')) return plasticBagWrapperIcon;
    if (name.includes('plastic') && name.includes('container')) return plasticContainerIcon;
    if (name.includes('carton') && name.includes('drink')) return drinkCartonIcon;
    if (name.includes('juice')) return juicePackageIcon;
    if (name.includes('gloves')) return rubberGlovesIcon;
    if (name.includes('cardboard')) return cardboardIcon;
    if (name.includes('organic')) return organicIcon;
    if (name.includes('meat') || name.includes('food')) return meatIcon;
    if (name.includes('container')) return containerIcon;
    if (name.includes('paper')) return paperIcon;
    if (name.includes('metal')) return metalIcon;
    if (name.includes('plastic')) return plasticIcon;
    return recycleIcon;
  };

  const getColorClass = (index) => {
    const colors = [
      'bg-blue-100 border border-transparent dark:bg-blue-500/30 dark:border-blue-400/50',
      'bg-emerald-100 border border-transparent dark:bg-emerald-500/30 dark:border-emerald-400/50',
      'bg-rose-100 border border-transparent dark:bg-rose-500/30 dark:border-rose-400/50',
      'bg-cyan-100 border border-transparent dark:bg-cyan-500/30 dark:border-cyan-400/50',
      'bg-purple-100 border border-transparent dark:bg-purple-500/30 dark:border-purple-400/50',
      'bg-amber-100 border border-transparent dark:bg-amber-500/30 dark:border-amber-400/50',
      'bg-yellow-100 border border-transparent dark:bg-yellow-500/25 dark:border-yellow-400/50',
      'bg-pink-100 border border-transparent dark:bg-pink-500/30 dark:border-pink-400/50',
      'bg-indigo-100 border border-transparent dark:bg-indigo-500/30 dark:border-indigo-400/50',
      'bg-orange-100 border border-transparent dark:bg-orange-500/30 dark:border-orange-400/50',
      'bg-teal-100 border border-transparent dark:bg-teal-500/30 dark:border-teal-400/50',
      'bg-lime-100 border border-transparent dark:bg-lime-500/25 dark:border-lime-400/50'
    ];
    return colors[index % colors.length];
  };

  const getSizeTier = (percentage) => {
    if (percentage >= 25) return 'xl';
    if (percentage >= 15) return 'lg';
    if (percentage >= 8)  return 'md';
    if (percentage >= 3)  return 'sm';
    return 'xs';
  };

  const getCardSize = (percentage) => {
    switch (getSizeTier(percentage)) {
      case 'xl': return 'col-span-2 row-span-2 md:col-span-3 md:row-span-2';
      case 'lg': return 'col-span-2 row-span-1 md:col-span-3 md:row-span-1';
      case 'md': return 'col-span-2 row-span-1 md:col-span-2 md:row-span-1';
      case 'sm': return 'col-span-1 row-span-1 md:col-span-2 md:row-span-1';
      case 'xs': return 'col-span-1 row-span-1 md:col-span-1 md:row-span-1';
      default:   return 'col-span-1 row-span-1 md:col-span-1 md:row-span-1';
    }
  };

  const getContentConfig = (percentage) => {
    switch (getSizeTier(percentage)) {
      case 'xl': return { padding: 'p-4', iconSize: 'w-8 h-8', badgeSize: 'text-sm px-2.5 py-0.5', gap: 'gap-3', valueStyle: { fontSize: '1.5rem' } };
      case 'lg': return { padding: 'p-3', iconSize: 'w-7 h-7', badgeSize: 'text-xs px-2 py-0.5', gap: 'gap-2', valueStyle: { fontSize: '1.25rem' } };
      case 'md': return { padding: 'p-3', iconSize: 'w-6 h-6', badgeSize: 'text-xs px-2 py-0.5', gap: 'gap-2', valueStyle: { fontSize: '1.125rem' } };
      case 'sm': return { padding: 'p-2', iconSize: 'w-5 h-5', badgeSize: 'text-[9px] px-1.5 py-0', gap: 'gap-1', valueStyle: { fontSize: '0.875rem' } };
      case 'xs': return { padding: 'p-2', iconSize: 'w-4 h-4', badgeSize: 'text-[8px] px-1 py-0', gap: 'gap-1', valueStyle: { fontSize: '0.75rem' } };
      default:   return { padding: 'p-2', iconSize: 'w-5 h-5', badgeSize: 'text-[9px] px-1.5 py-0', gap: 'gap-1', valueStyle: { fontSize: '0.875rem' } };
    }
  };

  const shouldFilterWasteType = (wasteType) => {
    if (!wasteType) return true;
    const normalized = wasteType.toLowerCase().trim();
    return normalized === 'nothing' || normalized === '';
  };

  const normalizeAndCombineWasteTypes = (name) => {
    if (!name) return '';
    const normalized = name.toLowerCase().replace(/[_-]/g, '').trim();
    if (normalized === 'nothing') return '';
    const combinedMap = {
      'pmd': 'PMD',
      'residual': t('residuallabel') || 'Residual',
      'rest': t('residuallabel') || 'Residual',
      'papercardboard': t('paperlabel') || 'Paper/Cardboard',
      'paper': t('paperlabel') || 'Paper/Cardboard',
      'organic': t('organiclabel') || 'Organic',
      'glass': 'Glass'
    };
    return combinedMap[normalized] || name.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  };

  const normalizeAndFormatSubclassName = (name) => {
    if (!name) return '';
    const cleanName = name.toLowerCase().replace(/[_\-\s]/g, '');
    const specificTransformations = {
      'organicmandarin': 'Mandarin',
      'organicteabag': 'Teabag',
      'organic': 'Organic (other)',
      'pmd': 'PMD (Other)',
      'plasticbottle': t('plasticBottle') || 'Plastic Bottle',
      'metalcan': t('metalCan') || 'Metal Can',
      'plasticbag': t('plasticBag') || 'Plastic Bag',
      'plasticbagcolor': t('plasticBagColor') || 'Plastic Bag Color',
      'paperbox': t('paperBox') || 'Paper Box',
    };
    if (specificTransformations[cleanName]) return specificTransformations[cleanName];
    if (cleanName.startsWith('organic') && cleanName.length > 7) {
      const stripped = name.replace(/^organic\s*/i, '');
      return stripped.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').trim();
    }
    return name
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]/g, ' ')
      .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
      .trim();
  };

  useEffect(() => {
    const fetchWasteTypeWeights = async () => {
      try {
        let binIdentifiers = Array.isArray(id) ? id : (typeof id === 'string' && id.includes(',') ? id.split(',') : [id]);
        const bins = binIdentifiers.map(b => String(b).trim()).join(',');
        const timeRangeMap = { 'day': 'Day', 'week': 'Week', 'month': 'Month', '3months': '3Month', '6months': '6Month', 'year': 'Year' };
        const normalizedDayValue = dayvalue.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
        const timeRange = timeRangeMap[normalizedDayValue] || 'Month';
        const currentMonth = new Date().getMonth() + 1;
        let season = (currentMonth >= 3 && currentMonth <= 5) ? 'spring' : (currentMonth >= 6 && currentMonth <= 8) ? 'summer' : (currentMonth >= 9 && currentMonth <= 11) ? 'autumn' : 'winter';
        const params = { bins, timeRange, season };
        if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
          const sDate = new Date(datevalue[0].startDate);
          const eDate = new Date(datevalue[0].endDate);
          if (!isNaN(sDate) && !isNaN(eDate)) {
            params.start_date = sDate.toISOString();
            params.end_date = eDate.toISOString();
          }
        }
        const response = await axiosInstance.get('/others_page_waste_composition', { params });
        if (response.data) {
          const weights = {};
          if (response.data.paper    !== undefined) weights[t('paperlabel')    || 'Paper/Cardboard'] = response.data.paper;
          if (response.data.pmd      !== undefined) weights['PMD']                                   = response.data.pmd;
          if (response.data.residual !== undefined) weights[t('residuallabel') || 'Residual']        = response.data.residual;
          if (response.data.organic  !== undefined) weights[t('organiclabel')  || 'Organic']         = response.data.organic;
          if (response.data.glass    !== undefined) weights['Glass']                                 = response.data.glass;
          setWasteTypeWeights(weights);
          setActualTotalWeight(response.data.total || 0);
        }
      } catch (error) {
        console.error('Error fetching weights:', error);
        setWasteTypeWeights({});
        setActualTotalWeight(0);
      }
    };
    fetchWasteTypeWeights();
    const interval = setInterval(fetchWasteTypeWeights, 30000);
    return () => clearInterval(interval);
  }, [id, dayvalue, isCustomDateRange, datevalue, t]);

  useEffect(() => {
    let isFirstLoad = true;
    const fetchSubclassData = async () => {
      try {
        if (isFirstLoad) setLoading(true);
        setError(null);
        let binIdentifiers = Array.isArray(id) ? id : (typeof id === 'string' && id.includes(',') ? id.split(',') : [id]);
        binIdentifiers = binIdentifiers.map(id => typeof id === 'string' ? id.trim() : String(id)).filter(id => id.length > 0);
        const binResponse = await axiosInstance.get('/get_bins');
        const validBins = binIdentifiers.map(identifier => {
          let bin = binResponse.data.find(b => String(b.unique_identifier).toLowerCase() === String(identifier).toLowerCase());
          if (!bin && !isNaN(identifier)) bin = binResponse.data.find(b => b.id === parseInt(identifier));
          return bin;
        }).filter(b => b !== undefined);
        if (validBins.length === 0) throw new Error('No bins found');
        const binIds = validBins.map(bin => bin.id).join(',');
        let subclassUrl = `/subclass_waste_composition/${binIds}/${dayvalue.toLowerCase()}/`;
        const subclassParams = new URLSearchParams();
        if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
          const sDate = datevalue[0].startDate instanceof Date ? datevalue[0].startDate : new Date(datevalue[0].startDate);
          const eDate = datevalue[0].endDate instanceof Date ? datevalue[0].endDate : new Date(datevalue[0].endDate);
          if (!isNaN(sDate) && !isNaN(eDate)) {
            subclassParams.append('start_date', sDate.toISOString());
            subclassParams.append('end_date', eDate.toISOString());
          }
        }
        if (subclassParams.toString()) subclassUrl += `?${subclassParams.toString()}`;
        const subclassResponse = await axiosInstance.get(subclassUrl);
        if (!subclassResponse.data) { setSubclassData({}); return; }
        const combinedData = {};
        Object.keys(subclassResponse.data).forEach(wasteType => {
          if (shouldFilterWasteType(wasteType)) return;
          const normalizedType = normalizeAndCombineWasteTypes(wasteType);
          if (!normalizedType) return;
          if (!combinedData[normalizedType]) combinedData[normalizedType] = {};
          Object.keys(subclassResponse.data[wasteType]).forEach(subClass => {
            if (shouldFilterWasteType(subClass)) return;
            const normalizedSubClass = normalizeAndFormatSubclassName(subClass);
            const raw = subclassResponse.data[wasteType][subClass];

            // Backend now returns { weight, clean_weight, contaminated_weight }
            // per subclass; fall back to a bare number for safety.
            const weight = typeof raw === 'object' ? (raw.weight ?? 0) : (raw ?? 0);
            const cleanWeight = typeof raw === 'object' ? (raw.clean_weight ?? weight) : weight;
            const contaminatedWeight = typeof raw === 'object' ? (raw.contaminated_weight ?? 0) : 0;

            if (normalizedSubClass && weight > 0) {
              if (!combinedData[normalizedType][normalizedSubClass]) {
                combinedData[normalizedType][normalizedSubClass] = { weight: 0, cleanWeight: 0, contaminatedWeight: 0 };
              }
              combinedData[normalizedType][normalizedSubClass].weight += weight;
              combinedData[normalizedType][normalizedSubClass].cleanWeight += cleanWeight;
              combinedData[normalizedType][normalizedSubClass].contaminatedWeight += contaminatedWeight;
            }
          });
        });
        setSubclassData(combinedData);
      } catch (err) {
        if (err.response && err.response.status === 404) setSubclassData({});
        else { console.error('Error subclass data:', err); setError(err.message); }
      } finally {
        if (isFirstLoad) { setLoading(false); isFirstLoad = false; }
      }
    };
    fetchSubclassData();
    const interval = setInterval(fetchSubclassData, 30000);
    return () => clearInterval(interval);
  }, [dayvalue, id, isCustomDateRange, datevalue, t]);
  
  useEffect(() => {
    if (!subclassData) { setWasteData([]); return; }
    const calculatedItems = [];
    Object.keys(subclassData).forEach(categoryName => {
      const categoryItems = subclassData[categoryName];
      if (Object.keys(categoryItems).length > 0) {
        Object.keys(categoryItems).forEach(itemName => {
          const itemEntry = categoryItems[itemName];
          const itemRealWeight = itemEntry.weight;
          if (itemRealWeight < 0.001) return;
          const percentageOfTotal    = actualTotalWeight > 0 ? (itemRealWeight / actualTotalWeight) * 100 : 0;
          const percentageOfCategory = wasteTypeWeights[categoryName] > 0 ? (itemRealWeight / wasteTypeWeights[categoryName]) * 100 : 0;
          calculatedItems.push({
            name: itemName,
            weight: itemRealWeight,
            cleanWeight: itemEntry.cleanWeight,
            contaminatedWeight: itemEntry.contaminatedWeight,
            percentageOfTotal,
            percentageOfCategory,
            wasteType: categoryName,
          });
        });
      }
    });
    setWasteData(calculatedItems.sort((a, b) => b.weight - a.weight));
  }, [subclassData, wasteTypeWeights, actualTotalWeight]);

  const availableCategories = useMemo(() => {
    const categories = new Set(wasteData.map(item => item.wasteType));
    return ['All', ...Array.from(categories).sort()];
  }, [wasteData]);

  const filteredWasteData = useMemo(() => {
    if (selectedCategory === 'All') return wasteData;
    return wasteData.filter(item => item.wasteType === selectedCategory);
  }, [wasteData, selectedCategory]);

  const filteredTotalWeight = useMemo(() => {
    if (selectedCategory === 'All') return wasteData.reduce((acc, curr) => acc + curr.weight, 0);
    return wasteTypeWeights[selectedCategory] || 0;
  }, [selectedCategory, wasteData, wasteTypeWeights]);

  const displayDateText = useMemo(() => {
    if (isCustomDateRange) {
      if (datevalue?.[0]?.startDate && datevalue?.[0]?.endDate) {
        const options = { month: 'numeric', day: 'numeric', year: 'numeric' };
        const start = new Date(datevalue[0].startDate).toLocaleDateString('en-US', options);
        const end   = new Date(datevalue[0].endDate).toLocaleDateString('en-US', options);
        return `${start} - ${end}`;
      }
      return t('customRange'); 
    }
    const rawText = t(dayvalue.toLowerCase()) || dayvalue;
    return typeof rawText === 'string' 
      ? rawText.charAt(0).toUpperCase() + rawText.slice(1) 
      : rawText;
  }, [isCustomDateRange, datevalue, dayvalue, t]);

  return (
    <>
    {/* exportRef wraps the entire card; also registered as exportable-section for DashboardExport */}
    <div
      ref={exportRef}
      className={`exportable-section flex flex-col h-full w-full overflow-hidden dark:text-gray-100 ${className}`}
      data-export-data={exportDataAttr ?? undefined}
    >
      
      {/* HEADER */}
      <div className="flex-shrink-0 mb-2 px-4 pt-4 z-10">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="scroll-m-20 text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {t('subwastecardlabel') || 'Sub-waste Heatmap'}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">
                {displayDateText}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* ── Export dropdown ── */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-primary"
                    disabled={!wasteData.length}
                  >
                    <Download className="h-4 w-4 mr-1"/>
                    {t('export') || 'Export'}
                    <ChevronDown className="h-4 w-4 ml-1"/>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportCSV} disabled={!wasteData.length}>
                    {t('Download as CSV') || 'Download as CSV'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportPNG} disabled={exporting || !wasteData.length}>
                    {exporting ? 'Exporting...' : (t('Download as PNG') || 'Download as PNG')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-help flex items-center justify-center p-1">
                      <CircleHelp className="h-5 w-5 text-muted-foreground hover:text-primary transition-colors dark:text-gray-400 dark:hover:text-primary" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">
                    <p>A visual breakdown of the sub-waste categories. Card size reflects each item's share of total weight. Hover cards to see related images. A check mark means the item was sorted correctly everywhere in the current selection; an X means it was a contamination at least once, worth checking per-bin if that's unexpected.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1 border border-gray-200 dark:border-slate-700">
                <button onClick={() => setDisplayMode('kg')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${displayMode === 'kg' ? 'bg-white dark:bg-slate-600 text-primary shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}>KG</button>
                <button onClick={() => setDisplayMode('percentage')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${displayMode === 'percentage' ? 'bg-white dark:bg-slate-600 text-primary shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}>%</button>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 mask-linear-fade">
              {availableCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
                    selectedCategory === cat
                      ? 'bg-primary text-white dark:text-slate-900 border-primary shadow-md'
                      : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:text-primary hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {cat === 'All'
                    ? <span className="flex items-center gap-1"><Layers className="w-3 h-3"/> {t('All') || 'All'}</span>
                    : cat
                  }
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* CARD GRID */}
      <div 
        ref={gridRef}
        className="flex-1 min-h-0 overflow-y-auto px-4 pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent"
      >
        {loading ? (
          // Skeleton — same masonry-treemap shape as the loaded grid: a mix
          // of card sizes (matching getCardSize's xl/lg/md/sm/xs tiers) so
          // it reads as "a treemap is coming," not a generic spinner.
          <div
            className="grid grid-cols-2 md:grid-cols-6 auto-rows-[140px] gap-3 grid-flow-dense pb-4 motion-safe:animate-pulse"
            aria-busy="true"
          >
            <span className="sr-only" role="status">{t('loading') || 'Loading…'}</span>
            {[
              'col-span-2 row-span-2 md:col-span-3 md:row-span-2',
              'col-span-2 row-span-1 md:col-span-3 md:row-span-1',
              'col-span-1 row-span-1 md:col-span-2 md:row-span-1',
              'col-span-1 row-span-1 md:col-span-1 md:row-span-1',
              'col-span-1 row-span-1 md:col-span-1 md:row-span-1',
              'col-span-2 row-span-1 md:col-span-2 md:row-span-1',
            ].map((sizeClass, i) => (
              <div
                key={i}
                className={`${sizeClass} rounded-2xl bg-gray-100 dark:bg-slate-800 flex flex-col justify-between p-3`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-6 h-6 rounded-xl bg-gray-200 dark:bg-slate-700" />
                  <div className="h-4 w-10 rounded-full bg-gray-200 dark:bg-slate-700" />
                </div>
                <div className="space-y-1.5">
                  <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-slate-700" />
                  <div className="h-4 w-1/2 rounded bg-gray-200 dark:bg-slate-700" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-red-500">
            {t('errorLoadingData') || 'Error loading data'}
          </div>
        ) : filteredWasteData.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-6 auto-rows-[140px] gap-3 grid-flow-dense pb-4">
            {filteredWasteData.map((item, index) => {
              const relativePercentage = filteredTotalWeight > 0
                ? (item.weight / filteredTotalWeight) * 100
                : 0;
              const cardSizeClass = getCardSize(relativePercentage);
              const config = getContentConfig(relativePercentage);
              const iconPath = getIconPath(item.name);
              const hasImages = isAdmin && (
                allMedia.some(m => mediaMatchesSubclass(m, item.name)) ||
                allCleanMedia.some(m => mediaMatchesSubclass(m, item.name))
              );

              // Contamination badge: if this subclass has ANY contaminated
              // weight in the current selection, show X — even if some of
              // it was also correctly sorted elsewhere. This surfaces rule
              // conflicts between bins (e.g. one bin force_cleans an item
              // another bin doesn't) rather than hiding them; seeing an
              // unexpected X nudges the user to check per-bin and notice
              // the disagreement. Only show a clean check when there's no
              // contaminated weight at all.
              let contaminationStatus = null; // 'clean' | 'contaminated' | null
              if (item.contaminatedWeight > 0) {
                contaminationStatus = 'contaminated';
              } else if (item.cleanWeight > 0) {
                contaminationStatus = 'clean';
              }

              return (
                <div
                  key={`${item.wasteType}-${item.name}`}
                  className={`relative ${getColorClass(index)} ${cardSizeClass} ${config.padding} rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between overflow-hidden`}
                  style={{ cursor: hasImages ? 'pointer' : 'default' }}
                  onMouseEnter={(e) => isAdmin && showOverlay(item.name, item.wasteType, e)}
                  onMouseLeave={scheduleHideOverlay}
                  onMouseMove={(e) => {
                    if (imageOverlay?.visible) {
                      clearTimeout(overlayHideTimer.current);
                      setImageOverlay(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : prev);
                    }
                  }}
                >
                  <div className={`flex items-center justify-between min-w-0 ${config.gap}`}>
                    <div className="flex-shrink-0 bg-white/60 dark:bg-white/90 backdrop-blur-sm rounded-xl p-1.5 shadow-sm border border-white/40 dark:border-white/20">
                      <img
                        src={iconPath}
                        alt={item.name}
                        className={`${config.iconSize} object-contain transition-all`}
                        onError={(e) => { e.target.src = recycleIcon; }}
                      />
                    </div>
                    <span
                      className={`flex-shrink-0 inline-flex items-center justify-center font-bold bg-white/70 dark:bg-slate-900/60 backdrop-blur-md rounded-full text-gray-700 dark:text-white whitespace-nowrap border border-white/40 dark:border-white/10 ${config.badgeSize}`}
                      style={{ lineHeight: '1.6' }}
                    >
                      {displayMode === 'kg'
                        ? `${relativePercentage.toFixed(1).replace('.', ',')}%`
                        : `${item.weight.toFixed(3).replace('.', ',')} kg`
                      }
                    </span>
                  </div>

                  <div className="min-w-0 mt-auto pb-1.5">
                    <h3
                      className="font-semibold text-gray-800 dark:text-gray-50 leading-normal block overflow-visible whitespace-nowrap text-ellipsis w-full pb-0.5"
                      style={{ fontSize: relativePercentage >= 8 ? '0.875rem' : '0.75rem' }}
                      title={item.name}
                    >
                      {item.name}
                    </h3>
                    {selectedCategory === 'All' && (
                      <div
                        className="text-gray-500 dark:text-gray-300 font-semibold uppercase tracking-wider block overflow-visible whitespace-nowrap text-ellipsis pb-0.5"
                        style={{ fontSize: '0.625rem', lineHeight: '1.4' }}
                      >
                        {item.wasteType}
                      </div>
                    )}
                    <div className="flex items-baseline gap-1 mt-0.5 min-w-0 overflow-visible">
                      <span
                        className="font-bold text-gray-900 dark:text-white leading-normal block pb-1"
                        style={config.valueStyle}
                      >
                        {displayMode === 'kg' ? item.weight.toFixed(3).replace('.', ',') : relativePercentage.toFixed(1).replace('.', ',')}
                      </span>
                      <span className="text-[10px] text-gray-600 dark:text-gray-300 font-medium flex-shrink-0 block pb-1">
                        {displayMode === 'kg' ? 'kg' : '%'}
                      </span>
                    </div>
                  </div>

                  {(hasImages || contaminationStatus) && (
                    <div className="absolute bottom-2 right-2 flex items-center gap-1">
                      {contaminationStatus && (
                        <div
                          className="bg-white/60 dark:bg-white/90 backdrop-blur-sm rounded-xl p-1.5 shadow-sm border border-white/40 dark:border-white/20"
                          title={contaminationStatus === 'clean' ? 'Correct stream' : 'Contamination'}
                        >
                          {contaminationStatus === 'clean'
                            ? <Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
                            : <X className="w-3 h-3 text-rose-600" strokeWidth={3} />
                          }
                        </div>
                      )}
                      {hasImages && (
                        <div className="bg-white/60 dark:bg-white/90 backdrop-blur-sm rounded-xl p-1.5 shadow-sm border border-white/40 dark:border-white/20">
                          <Camera className="w-3 h-3 text-gray-500 dark:text-gray-600" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 text-sm gap-2">
            <Layers className="w-8 h-8 opacity-20"/>
            <p>{t('noDataForCategory') || 'No items found in this category'}</p>
          </div>
        )}
      </div>

      <div className="flex-shrink-0 pt-3 mt-auto border-t border-gray-100 dark:border-slate-800 px-4 pb-4 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm z-10">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold">
              {selectedCategory === 'All' ? (t('total') || 'Total') : selectedCategory}:
            </span>
            <span className="text-lg font-extrabold text-primary">
              {displayMode === 'kg' ? (
                <>{filteredTotalWeight.toFixed(3).replace('.', ',')} <span className="text-sm font-medium text-gray-500 dark:text-gray-400">kg</span></>
              ) : (
                <>100,0 <span className="text-sm font-medium text-gray-500 dark:text-gray-400">%</span></>
              )}
            </span>
          </div>
          <div className="text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-slate-800 px-2 py-1 rounded-md border border-gray-100 dark:border-slate-700">
            {filteredWasteData.length} {t('items') || 'items'}
          </div>
        </div>
      </div>
    </div>

    {/* ── Image overlay ──────────────────────────────────────────────────────── */}
    {imageOverlay?.visible && (
      <div
        className="subclass-overlay-ignore"
        onMouseEnter={cancelHideOverlay}
        onMouseLeave={scheduleHideOverlay}
        style={{
          position: 'fixed',
          left: Math.min(imageOverlay.x + 16, window.innerWidth - 290),
          top:  Math.min(imageOverlay.y - 10, window.innerHeight - 380),
          zIndex: 10000,
          background: isDarkMode ? 'rgba(20,20,20,0.97)' : 'rgba(255,255,255,0.97)',
          border: isDarkMode ? '1px solid #444' : '1px solid #ddd',
          borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          padding: 12,
          width: 270,
          pointerEvents: 'auto',
          fontSize: 13,
          color: isDarkMode ? '#fff' : '#111',
          lineHeight: 1.5,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{imageOverlay.subclassName}</div>
            <div style={{ fontSize: 11, color: isDarkMode ? '#aaa' : '#777' }}>{imageOverlay.wasteType}</div>
          </div>
          <button
            onClick={() => setImageOverlay(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDarkMode ? '#aaa' : '#666', padding: 2 }}
          >
            <X size={14} />
          </button>
        </div>

        <div style={{ borderTop: isDarkMode ? '1px solid #333' : '1px solid #eee', marginBottom: 8 }} />
        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, color: isDarkMode ? '#ccc' : '#555' }}>
          Images
        </div>

        {imageOverlay.images.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: isDarkMode ? '#888' : '#999', fontSize: 12, padding: '4px 0' }}>
            <ImageOff size={14} /> No images for this period
          </div>
        ) : (
          <>
            <div
              onScroll={(e) => {
                const el = e.currentTarget;
                if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
                  setVisibleCount(prev => Math.min(prev + 9, imageOverlay.images.length));
                }
              }}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, maxHeight: 200, overflowY: 'auto' }}
            >
              {imageOverlay.images.slice(0, visibleCount).map((img, idx) => {
                const src = signedUrls[img.file_path];
                const loadingThumb = img.file_path && !(img.file_path in signedUrls);
                return (
                  <button
                    key={img.id || idx}
                    onClick={() => setLightbox({ items: imageOverlay.images, index: idx })}
                    style={{
                      position: 'relative', aspectRatio: '1', borderRadius: 6, overflow: 'hidden',
                      background: isDarkMode ? '#333' : '#f0f0f0', border: 'none',
                      cursor: 'pointer', padding: 0,
                    }}
                    title={img.label || ''}
                  >
                    {src ? (
                      img.media_type === 'video'
                        ? <video src={src} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <img src={src} alt={img.label || 'media'} loading="lazy"
                               style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : loadingThumb ? (
                      <div className="animate-pulse w-full h-full" style={{ background: isDarkMode ? '#444' : '#e0e0e0' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ImageOff size={14} style={{ color: isDarkMode ? '#666' : '#bbb' }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <p style={{ marginTop: 6, fontSize: 10, color: isDarkMode ? '#666' : '#aaa' }}>
              Showing {Math.min(visibleCount, imageOverlay.images.length)} of {imageOverlay.images.length} · click to enlarge
            </p>
          </>
        )}
      </div>
    )}

    {/* Lightbox */}
    {lightbox && (
      <Lightbox
        items={lightbox.items}
        index={lightbox.index}
        onClose={() => setLightbox(null)}
        onPrev={() => setLightbox(prev => ({ ...prev, index: (prev.index - 1 + prev.items.length) % prev.items.length }))}
        onNext={() => setLightbox(prev => ({ ...prev, index: (prev.index + 1) % prev.items.length }))}
        signedUrls={signedUrls}
        onRequestSign={requestSign}
      />
    )}
    </>
  );
};

export default SubclassCardView;